import React, { useEffect, useState } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaMapMarkerAlt,
  FaMedal,
  FaShieldAlt,
  FaStar,
  FaGlobeAmericas,
  FaClock,
  FaTruck,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const normalize = (value) =>
  value
    ? value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    : '';

const PORT_CITIES = ['manzanillo', 'veracruz', 'lazaro cardenas', 'altamira', 'nuevo laredo'];
const EXPRESS_KEYWORDS = ['express', 'rápido', 'rapido', 'puerta'];
const COLD_CHAIN_KEYWORDS = ['fresh', 'fría', 'fria', 'polar', 'refrigerada', 'cold', 'haccp'];
const SPECIALIZED_KEYWORDS = ['prime', 'secure', 'custod', 'torque', 'rail', 'intermodal'];

export const FILTER_OPTIONS = [
  { label: 'Todos', matcher: () => true },
  {
    label: 'Puertos',
    matcher: (service) => {
      const city = normalize(service?.ubic);
      return PORT_CITIES.some((port) => city.includes(port));
    },
  },
  {
    label: 'Rutas terrestres',
    matcher: (service) => {
      const city = normalize(service?.ubic);
      return !PORT_CITIES.some((port) => city.includes(port));
    },
  },
  {
    label: 'Cadena fría',
    matcher: (service) => {
      const normalized = normalize(`${service?.nombre ?? ''} ${service?.desc ?? ''}`);
      return COLD_CHAIN_KEYWORDS.some((keyword) => normalized.includes(keyword));
    },
  },
  {
    label: 'Carga especializada',
    matcher: (service) => {
      const normalized = normalize(`${service?.nombre ?? ''} ${service?.desc ?? ''}`);
      return SPECIALIZED_KEYWORDS.some((keyword) => normalized.includes(keyword));
    },
  },
  {
    label: 'Entregas express',
    matcher: (service) => {
      const normalized = normalize(`${service?.nombre ?? ''} ${service?.desc ?? ''}`);
      return EXPRESS_KEYWORDS.some((keyword) => normalized.includes(keyword));
    },
  },
];

const AVATAR_COLORS = ['#c7d2fe', '#bae6fd', '#bbf7d0', '#fde68a', '#fecdd3', '#e9d5ff'];

const toNumber = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const formatPrice = (raw) => {
  if (raw === null || raw === undefined) return 'Cotiza ahora';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(raw);
  }
  const asString = String(raw).trim();
  if (!asString) return 'Cotiza ahora';
  const extracted = asString.replace(/[^0-9.,-]/g, '');
  const normalized = extracted.replace(/,/g, '');
  const numeric = Number(normalized);
  if (Number.isFinite(numeric)) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(numeric);
  }
  return asString;
};

const getAvatarColor = (value) => {
  const safe = normalize(value);
  if (!safe) return AVATAR_COLORS[0];
  const hash = safe.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const getRatingValue = service => {
  const sources = [
    service?.company?.rating,
    service?.companyRating,
    service?.company?.score,
  ];
  for (const source of sources) {
    const numeric = toNumber(source);
    if (numeric) {
      return numeric.toFixed(1);
    }
  }
  return '4.8';
};

const sanitizeProviderLabel = value => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return '';
  const normalized = trimmed.toLowerCase();
  if (normalized === 'proveedor' || normalized === 'provider') return '';
  return trimmed;
};

export const FeedCard = ({ service }) => {
  const providerLabel = sanitizeProviderLabel(service?.providerName)
  const truncatedName =
    typeof service?.nombre === 'string' && service.nombre.length > 48
      ? `${service.nombre.slice(0, 45).trim()}…`
      : service?.nombre;
  const rawImage = typeof service?.img === 'string' ? service.img.trim() : '';
  const hasCoverImage = rawImage.length > 0;
  const route = service?.ubic && service.ubic.trim() ? service.ubic : 'Queretaro, QUE, MX';
  const basePriceLabel = formatPrice(service?.precio);
  const priceLabel = basePriceLabel === 'Cotiza ahora' ? 'Consulta precio' : basePriceLabel;
  const actionHref = service?.href || '/marketplace';
  const ratingValue = getRatingValue(service);
  const providerDisplay = providerLabel;

  return (
    <article className="listing-card" role="listitem">
      <div className={`listing-card__media${hasCoverImage ? '' : ' listing-card__media--empty'}`}>
        {hasCoverImage && (
          <img
            src={rawImage}
            alt={`Operación de ${service?.nombre || 'proveedor en Flotte'}`}
            loading="lazy"
          />
        )}
        <span className="listing-card__badge">Alta demanda</span>
        <span className="listing-card__rating">
          <strong>{ratingValue}</strong>
          <span>Excelente</span>
        </span>
      </div>

      <div className="listing-card__body">
        <div className="listing-card__content">
          <h3 title={truncatedName || 'Unidad disponible'}>{truncatedName || 'Unidad disponible'}</h3>
          <p className="listing-card__location">
            <FaMapMarkerAlt aria-hidden="true" />
            {route}
          </p>
          {providerDisplay && (
            <p className="listing-card__provider">
              <FaTruck aria-hidden="true" />
              {providerDisplay}
            </p>
          )}
        </div>

        <div className="listing-card__footer">
          <div className="listing-card__price">
            <span>Desde</span>
            <strong>{priceLabel}</strong>
          </div>
          <Link to={actionHref} className="listing-card__cta">
            Ver oferta
          </Link>
        </div>
      </div>
    </article>
  );
};

export const FeedCardPlaceholder = () => (
  <article className="listing-card listing-card--placeholder" role="presentation" aria-hidden="true">
    <div className="listing-card__media listing-card__media--placeholder">
      <span className="listing-card__skeleton listing-card__skeleton--media" />
    </div>
    <div className="listing-card__body">
      <div className="listing-card__content">
        <span className="listing-card__skeleton listing-card__skeleton--line" />
        <span className="listing-card__skeleton listing-card__skeleton--line short" />
        <span className="listing-card__skeleton listing-card__skeleton--line shorter" />
      </div>
      <div className="listing-card__footer">
        <span className="listing-card__skeleton listing-card__skeleton--line short" />
        <span className="listing-card__skeleton listing-card__skeleton--pill" />
      </div>
    </div>
  </article>
);

export const EliteCompanyCard = ({ company }) => {
  const profileHref =
    company?.profileHref ||
    (company?.slug ? `/empresa/${company.slug}` : '/marketplace');
  const heroUrl = company?.heroUrl || company?.logoUrl || '';
  const logoUrl = company?.logoUrl || '';
  const coverage = company?.coverage || '';
  const responseTime = company?.responseTime || '';
  const rating = typeof company?.rating === 'number' && Number.isFinite(company.rating)
    ? company.rating
    : null;
  const tagline =
    company?.tagline && company.tagline.trim()
      ? company.tagline.trim()
      : 'Proveedor Elite verificado en Flotte.';

  return (
    <article className="elite-card" role="listitem">
      <header className="elite-card__header">
        <span className="elite-card__badge">
          <FaMedal aria-hidden="true" />
          Elite
        </span>
        <span className="elite-card__tagline">{tagline}</span>
      </header>

      {(heroUrl || logoUrl) && (
        <figure className="elite-card__media">
          <img
            className="elite-card__media-img"
            src={heroUrl || logoUrl}
            alt={`Operaciones de ${company?.name || 'empresa'}`}
            loading="lazy"
          />
          {heroUrl && logoUrl && (
            <img
              className="elite-card__logo"
              src={logoUrl}
              alt={company?.name || 'Empresa'}
              loading="lazy"
            />
          )}
        </figure>
      )}

      <div className="elite-card__body">
        <h3 className="elite-card__title">{company?.name || 'Empresa Elite'}</h3>
        {company?.industry && <p className="elite-card__industry">{company.industry}</p>}
        <div className="elite-card__metrics">
          <span className="elite-card__metric">
            <FaShieldAlt aria-hidden="true" />
            Plan Elite activo
          </span>
          {coverage && (
            <span className="elite-card__metric">
              <FaGlobeAmericas aria-hidden="true" />
              {coverage}
            </span>
          )}
          {responseTime && (
            <span className="elite-card__metric">
              <FaClock aria-hidden="true" />
              {responseTime}
            </span>
          )}
          {rating !== null && (
            <span className="elite-card__metric elite-card__metric--rating">
              <FaStar aria-hidden="true" />
              {rating.toFixed(1)} calificación promedio
            </span>
          )}
        </div>
      </div>

      <footer className="elite-card__footer">
        <Link to={profileHref} className="elite-card__cta">
          <FaGlobeAmericas aria-hidden="true" />
          Ver perfil Elite
        </Link>
      </footer>
    </article>
  );
};

const FEATURED_PAGES = [
  {
    name: 'Alfa Logistics',
    industry: 'Transporte y almacenamiento',
    followers: 186,
  },
  {
    name: 'Mitre Fleet',
    industry: 'Infraestructura y mantenimiento',
    followers: 92,
  },
  {
    name: 'Nova Supply Chain',
    industry: 'Servicios 3PL',
    followers: 241,
  },
];

export const CategoriesPanel = ({ units }) => {
  const WINDOW_SIZE = 6;
  const safeUnits = Array.isArray(units) ? units : [];
  const [startIndex, setStartIndex] = useState(0);

  const totalUnits = safeUnits.length;
  const maxIndex = Math.max(0, totalUnits - WINDOW_SIZE);
  const hasCarousel = totalUnits > WINDOW_SIZE;

  useEffect(() => {
    setStartIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const visibleUnits = hasCarousel
    ? safeUnits.slice(startIndex, startIndex + WINDOW_SIZE)
    : safeUnits.slice(0, WINDOW_SIZE);

  const handlePrev = () => setStartIndex((prev) => Math.max(prev - 1, 0));
  const handleNext = () => setStartIndex((prev) => Math.min(prev + 1, maxIndex));

  const rangeStart = totalUnits === 0 ? 0 : startIndex + 1;
  const rangeEnd = totalUnits === 0 ? 0 : Math.min(startIndex + WINDOW_SIZE, totalUnits);

  return (
    <article className="info-card" role="list">
      <header className="info-card__header">
        <h3 className="info-card__title">Categorías disponibles</h3>
        <p className="info-card__subtitle">
          Encuentra el tipo de unidad ideal para tu operación y solicítala en segundos.
        </p>
      </header>
      <div className="categories-grid">
        {visibleUnits.map((unit) => (
          <div key={unit.tipo} className="categories-grid__item" role="listitem">
            <figure className="categories-grid__media">
              <img src={unit.img} alt={unit.tipo} loading="lazy" />
            </figure>
            <span className="categories-grid__label">{unit.tipo}</span>
          </div>
        ))}
      </div>

      {hasCarousel && (
        <div className="categories-grid__controls">
          <div className="categories-grid__nav" role="group" aria-label="Navegación de categorías">
            <button
              type="button"
              className="categories-grid__nav-btn"
              onClick={handlePrev}
              disabled={startIndex === 0}
              aria-label="Mostrar categorías anteriores"
            >
              <FaArrowLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              className="categories-grid__nav-btn"
              onClick={handleNext}
              disabled={startIndex >= maxIndex}
              aria-label="Mostrar categorías siguientes"
            >
              <FaArrowRight aria-hidden="true" />
            </button>
          </div>
          <span className="categories-grid__counter" aria-live="polite">
            {rangeStart}-{rangeEnd} de {totalUnits}
          </span>
        </div>
      )}

      <button type="button" className="info-card__cta">
        Ver todas las unidades
        <FaArrowRight aria-hidden="true" />
      </button>
    </article>
  );
};

export const RecentPublicationsPanel = ({ listings, loading }) => {
  if (loading && !listings.length) {
    return (
      <article className="info-card">
        <header className="info-card__header">
          <h3 className="info-card__title">Publicaciones destacadas</h3>
        </header>
        <p className="info-card__placeholder">Cargando publicaciones...</p>
      </article>
    );
  }

  if (!listings.length) {
    return (
      <article className="info-card">
        <header className="info-card__header">
          <h3 className="info-card__title">Publicaciones destacadas</h3>
        </header>
        <p className="info-card__placeholder">No hay publicaciones disponibles por ahora.</p>
      </article>
    );
  }

  return (
    <article className="info-card">
      <header className="info-card__header">
        <h3 className="info-card__title">Publicaciones destacadas</h3>
        <p className="info-card__subtitle">
          Lo último publicado por la comunidad flottera. Consulta los detalles y reserva.
        </p>
      </header>

      <ul className="recent-list">
        {listings.slice(0, 4).map((listing) => (
          <li key={listing.titulo} className="recent-list__item">
            <figure className="recent-list__media">
              <img src={listing.img} alt={listing.titulo} loading="lazy" />
            </figure>
            <div className="recent-list__body">
              <h4 className="recent-list__title">{listing.titulo}</h4>
              <p className="recent-list__meta">{listing.ubic}</p>
              <span className="recent-list__price">{listing.precio}</span>
              <Link to={listing.href || '/marketplace'} className="recent-list__cta">
                Ver publicación
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
};

export const MarketplaceShortcutCard = () => (
  <article className="info-card marketplace-card">
    <header className="info-card__header">
      <h3 className="info-card__title">Explora el marketplace</h3>
      <p className="info-card__subtitle">
        Descubre proveedores verificados, reserva unidades especializadas y publica tus servicios en minutos.
      </p>
    </header>

    <Link to="/marketplace" className="info-card__cta">
      Ir al marketplace
    </Link>
  </article>
);

export const FeaturedPagesPanel = ({ pages = FEATURED_PAGES }) => {
  const safePages = Array.isArray(pages) && pages.length ? pages : FEATURED_PAGES;

  return (
    <article className="info-card featured-pages">
      <header className="info-card__header featured-pages__header">
        <h3 className="info-card__title">Otras paginas destacadas</h3>
        <p className="info-card__subtitle">Sigue proveedores claves para estar al tanto de sus novedades.</p>
      </header>

      <ul className="featured-pages__list">
        {safePages.map((page) => {
          const initial = page?.name ? page.name.trim().charAt(0).toUpperCase() : 'F';
          return (
            <li key={page.name || page.industry} className="featured-pages__item">
              <span
                className="featured-pages__avatar"
                style={{ backgroundColor: getAvatarColor(page?.name) }}
                aria-hidden="true"
              >
                {initial}
              </span>

              <div className="featured-pages__body">
                <h4 className="featured-pages__name">{page?.name || 'Proveedor destacado'}</h4>
                <p className="featured-pages__meta">
                  {page?.industry || 'Operaciones logisticas'} - {page?.followers || 0} seguidores
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </article>
  );
};
